import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { setSelectedPatient } from '../../../ReduxSlices/Patients/patientSlice';

const PatientList = ({ patients, navigateTo }) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  const [currentPage, setCurrentPage] = useState(1);
  const patientsPerPage = 15;

  const dispatch = useDispatch();

  const filteredPatients = useMemo(() => {
    const term = searchTerm.toLowerCase();
   return patients.filter((patient) => {
    const name = patient.name?.toLowerCase() || "";
    const contact = patient.contact_info?.toLowerCase() || "";

    return name.includes(term) || contact.includes(term);
  });

  }, [patients, searchTerm]);

  const totalPages = Math.ceil(filteredPatients.length / patientsPerPage);
  const startIndex = (currentPage - 1) * patientsPerPage;
  const currentPatients = filteredPatients.slice(
    startIndex,
    startIndex + patientsPerPage,
  );

  const handlePrevious = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNext = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));

  if (!patients.length) return <p>No patients found.</p>;

  return (
    <div className="overflow-x-auto mt-4">
      <div className="relative mb-4 search">
        <input
          type="text"
          placeholder="Search by name or contact..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          className="px-4 py-2 pr-10 border border-gray-300 rounded w-full"
        />
        {searchTerm && (
        <button
          onClick={() => {
            setSearchTerm('');
            setCurrentPage(1);
          }}
          type="button"
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 text-lg font-bold focus:outline-none"
        >
          &times;
        </button>
        )}
      </div>

      <table className="list-table min-w-full bg-white border border-gray-200 shadow rounded border-collapse">
        <thead className="bg-gray-100 text-gray-700">
          <tr>
            <th className="px-8 py-4 border-b border-gray-300 text-left">#</th>
            <th className="px-8 py-4 border-b border-gray-300 text-left">Name</th>
            <th className="px-8 py-4 border-b border-gray-300 text-left">Age</th>
            <th className="px-8 py-4 border-b border-gray-300 text-left">Contact Info</th>
            <th className="px-8 py-4 border-b border-gray-300 text-left">Action</th>
          </tr>
        </thead>
        <tbody>
          {currentPatients.length > 0 ? (
            currentPatients.map((patient, index) => (
              <tr key={patient.id} className="hover:bg-gray-50 cursor-pointer" 
               onClick={() => dispatch(setSelectedPatient(patient))}
               onKeyDown={(e) =>
                e.key === "Enter" && dispatch(setSelectedPatient(patient))
              }>
                <td className="px-8 py-4 border-b border-gray-200">{startIndex + index + 1}</td>
                <td className="px-8 py-4 border-b border-gray-200">{patient.name}</td>
                <td className="px-8 py-4 border-b border-gray-200">{patient.age || '-'}</td>
                <td className="px-8 py-4 border-b border-gray-200">{patient.contact_info || '-'}</td>
                <td  className="px-8 py-4 border-b border-gray-200">
                    <button
                    onClick={(e) => {
                      e.stopPropagation();// prevent row click
                      dispatch(setSelectedPatient(patient)); // store patient to edit
                      navigateTo('new_patients');
                    }}
                    className='primary'                   
                    >
                      Edit info
                    </button>
                </td>                
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="4" className="text-center px-4 py-2">
                No matching patients found.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="flex justify-between items-center mt-4">
        <button
          type="button"
          onClick={handlePrevious}
          disabled={currentPage === 1}
          className="px-4 py-2 bg-gray-200 text-sm rounded disabled:opacity-50"
        >
          Previous
        </button>
        <span className="text-sm text-gray-700">
          Page
          {' '}
          {currentPage}
          {' '}
          of
          {' '}
          {totalPages}
        </span>
        <button
          onClick={handleNext}
          type="button"
          disabled={currentPage === totalPages}
          className="px-4 py-2 bg-gray-200 text-sm rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
};

PatientList.propTypes = {
  patients: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      age: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      contact_info: PropTypes.string,
    }),
  ).isRequired,
};

export default PatientList;
